/*
 * GA4 phase 3: module views and dwell time.
 *
 * No per-section markup: every section under #main-content (= every block in the
 * template) is scanned automatically, so adding / removing / reordering sections
 * in the theme editor needs no code change.
 *
 *   module_view  - a module has been "visible" for 1s in total; once per module per page view (reach)
 *   module_dwell - when the page is hidden / left, one event per module with the time accumulated since the last send
 *
 * "Visible" = the exposed height covers at least half the viewport OR at least half
 * of the module itself. Own-half alone never triggers for modules taller than one
 * screen (scroll-driven video); viewport-half alone never triggers for short ones.
 *
 * Every 0.5s the time goes to the single module covering the most viewport, so the
 * modules add up to the page's engaged time with no double counting. Nothing is
 * counted while the page is hidden, or after 60s without input unless a video in
 * that module is playing.
 *
 * Events go through window.__soulogGa4Event from theme.liquid (dataLayer -> GTM -> GA4).
 */
(function () {
	if (window.__soulogModuleTracking) return;
	window.__soulogModuleTracking = true;

	var TICK_MS = 500;
	var VIEW_MS = 1000;
	var IDLE_MS = 60000;
	var CAP_MS = 600000;
	var MIN_DWELL_MS = 1000;

	/* Module key: Shopify wraps each section as id="shopify-section-<template|group>__<key>";
	   <key> is the section key in templates/*.json and survives heading edits. The click
	   delegate in theme.liquid uses the same helper to tag clicks with module_id. */
	function moduleKey(section) {
		var id = (section && section.id) || "";
		var i = id.lastIndexOf("__");
		return i >= 0 ? id.slice(i + 2) : id.replace(/^shopify-section-/, "");
	}
	/* layout/theme.liquid defines the same helper inline (it must exist before this deferred
	   script loads, or early events lose module_id); only fill it in if that is missing. */
	if (typeof window.__soulogModuleOf !== "function") {
		window.__soulogModuleOf = function (el) {
			var section = el && el.closest ? el.closest(".shopify-section") : null;
			return section ? moduleKey(section) : "";
		};
	}

	/* Skip the theme editor preview: dragging sections around would create fake data */
	if (window.Shopify && window.Shopify.designMode) return;

	function headingOf(section, key) {
		/* A heading marked data-ga4-no-name is skipped: the product page's shop-module title became an h1 for SEO
		   (2026-11-03) but the module keeps its old name ("shop", it had no h1~h3 before), so the data doesn't break. */
		var h = section.querySelector("h1:not([data-ga4-no-name]), h2:not([data-ga4-no-name]), h3:not([data-ga4-no-name])");
		var text = "";
		if (h) {
			/* Read a clone without aria-hidden parts: animated copies (e.g. the launch hero
			   typewriter) are aria-hidden next to a full visually-hidden copy, and their
			   half-typed text would make the name differ per visitor. <br> becomes a space
			   so multi-line titles don't glue words together. */
			var clone = h;
			try {
				clone = h.cloneNode(true);
				var drop = clone.querySelectorAll('[aria-hidden="true"]');
				for (var i = 0; i < drop.length; i++) drop[i].parentNode.removeChild(drop[i]);
				var brs = clone.querySelectorAll("br");
				for (var j = 0; j < brs.length; j++) brs[j].parentNode.replaceChild(document.createTextNode(" "), brs[j]);
			} catch (e) {
				clone = h;
			}
			text = String(clone.textContent || "")
				.replace(/\s+/g, " ")
				.trim();
		}
		return text ? text.slice(0, 100) : key;
	}

	/* Sections pinned to the viewport (sticky / fixed - e.g. the Support "On this page" rail) stay
	   on screen while the visitor reads other sections and would win every dwell tick, so they get
	   views and clicks but no dwell - same as the footer. */
	function pinnedToViewport(section) {
		try {
			var pos = window.getComputedStyle ? window.getComputedStyle(section).position : "";
			return pos === "sticky" || pos === "fixed";
		} catch (e) {
			return false;
		}
	}

	var modules = [];
	function register(section, dwell) {
		var key = moduleKey(section);
		if (!key) return;
		modules.push({
			el: section,
			id: key,
			name: headingOf(section, key),
			position: String(modules.length + 1),
			dwell: dwell && !pinnedToViewport(section),
			visibleMs: 0,
			viewed: false,
			pendingMs: 0,
			totalMs: 0,
		});
	}
	var main = document.querySelectorAll("#main-content > .shopify-section");
	for (var i = 0; i < main.length; i++) register(main[i], true);
	/* Footer: reach only ("how many got to the bottom"), no dwell - it is navigation, not content */
	var footer = document.querySelectorAll(".shopify-section-group-footer-group");
	if (footer.length) register(footer[footer.length - 1], false);
	if (!modules.length) return;

	function params(m) {
		return { module_id: m.id, module_name: m.name, position: m.position };
	}

	/*
	 * Buy journey - "what did this buyer look at before ordering", written onto the order.
	 *
	 * Dwell and demo clicks are accumulated across pages in localStorage; at the moment of buying,
	 * a short summary goes into the hidden order attribute _soulog_journey (underscore = invisible
	 * to the customer at checkout, readable through the Admin API):
	 *   { v, pages: [page types in order], top: [3 longest-viewed modules], acts: [5 most-clicked
	 *     controls], gallery: [shop-module photos the visitor picked by hand, oldest first, max 6],
	 *     buy: { p: page, m: module, b: button }, ts }
	 * so marketing can see which selling point a buyer spent time on without asking them.
	 *
	 * Consent: same gate as GA4 analytics_storage in theme.liquid's consent bridge (no banner
	 * pending + analyticsProcessingAllowed). Fail-closed: if the privacy API is missing or says
	 * no, nothing is stored and nothing is written to the order.
	 */
	var JOURNEY_KEY = "soulog_journey";
	var JOURNEY_ATTR = "_soulog_journey";
	var JOURNEY_TTL = 30 * 864e5;
	var KEEP_PAGES = 12;
	var KEEP_MODS = 20;
	var KEEP_ACTS = 20;

	/* Page type from the path; an optional locale prefix (/fr, /pt-br) is ignored */
	function pageType() {
		var path = String((window.location && window.location.pathname) || "/").replace(
			/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i,
			"",
		);
		if (path === "" || path === "/") return "home";
		if (/^\/products\//.test(path)) return "product";
		if (/^\/cart/.test(path)) return "cart";
		var page = path.match(/^\/pages\/([^/?#]+)/);
		if (page) return page[1] === "subscription" ? "pricing" : "page:" + page[1];
		return "other";
	}
	var PAGE = pageType();

	function journeyAllowed() {
		try {
			var privacy = window.Shopify && window.Shopify.customerPrivacy;
			if (!privacy || typeof privacy.analyticsProcessingAllowed !== "function") return false;
			if (privacy.shouldShowBanner && privacy.shouldShowBanner()) return false;
			return !!privacy.analyticsProcessingAllowed();
		} catch (e) {
			return false;
		}
	}

	/* What this page view adds, not yet merged into storage */
	var pending = { mods: {}, acts: {}, gallery: [], pageCounted: false };
	var KEEP_GALLERY = 6;

	function readJourney() {
		try {
			var box = JSON.parse(window.localStorage.getItem(JOURNEY_KEY) || "null");
			if (box && box.v === 1 && Date.now() - box.start < JOURNEY_TTL) return box;
		} catch (e) {}
		return { v: 1, start: Date.now(), pages: [], mods: {}, acts: {} };
	}

	/* Keep only the biggest entries so storage stays small however long the visitor browses */
	function trim(map, size, field) {
		var keys = Object.keys(map);
		if (keys.length <= size) return map;
		keys.sort(function (a, b) {
			return (field ? map[b][field] : map[b]) - (field ? map[a][field] : map[a]);
		});
		var out = {};
		for (var i = 0; i < size; i++) out[keys[i]] = map[keys[i]];
		return out;
	}

	/* Merge this page's numbers into storage; returns the merged journey, or null without consent */
	function persistJourney() {
		if (!journeyAllowed()) return null;
		var box = readJourney();
		if (!pending.pageCounted) {
			if (box.pages[box.pages.length - 1] !== PAGE) box.pages.push(PAGE);
			if (box.pages.length > KEEP_PAGES) box.pages = box.pages.slice(-KEEP_PAGES);
			pending.pageCounted = true;
		}
		for (var mk in pending.mods) {
			var cur = box.mods[mk] || { n: pending.mods[mk].n, ms: 0 };
			cur.n = pending.mods[mk].n;
			cur.ms += pending.mods[mk].ms;
			box.mods[mk] = cur;
		}
		for (var ak in pending.acts) box.acts[ak] = (box.acts[ak] || 0) + pending.acts[ak];
		/* Gallery photos (2026-10-26): re-viewing one moves it to the end, so the last entry is the photo
		   seen right before buying - the cart page that starts checkout has no gallery of its own */
		var gallery = box.gallery || [];
		for (var gi = 0; gi < pending.gallery.length; gi++) {
			var file = pending.gallery[gi];
			var at = gallery.indexOf(file);
			if (at > -1) gallery.splice(at, 1);
			gallery.push(file);
		}
		box.gallery = gallery.slice(-KEEP_GALLERY);
		pending.mods = {};
		pending.acts = {};
		pending.gallery = [];
		box.mods = trim(box.mods, KEEP_MODS, "ms");
		box.acts = trim(box.acts, KEEP_ACTS);
		try {
			window.localStorage.setItem(JOURNEY_KEY, JSON.stringify(box));
		} catch (e) {}
		return box;
	}

	function trackDwell(m, ms) {
		var key = PAGE + ":" + m.id;
		var cur = pending.mods[key] || (pending.mods[key] = { n: m.name.slice(0, 40), ms: 0 });
		cur.ms += ms;
	}

	function trackAct(m, name) {
		var key = PAGE + ":" + m.id + "|" + String(name).slice(0, 40);
		pending.acts[key] = (pending.acts[key] || 0) + 1;
	}

	/* Called by assets/soulog-shop-module.js when a photo the visitor picked by hand has been on screen 1s */
	function noteGalleryImage(file) {
		if (file) pending.gallery.push(String(file).slice(0, 40));
	}

	function labelOf(el) {
		if (!el) return "";
		var name = (el.getAttribute && el.getAttribute("aria-label")) || "";
		if (!name) name = String(typeof el.innerText === "string" ? el.innerText : el.textContent || "");
		return name.replace(/\s+/g, " ").trim().slice(0, 40);
	}

	/* Summary for the order; "" when consent is missing. buyEl = the button that started the purchase */
	function snapshot(buyEl, buyLabel) {
		var box = persistJourney();
		if (!box) return "";
		var mods = Object.keys(box.mods)
			.sort(function (a, b) {
				return box.mods[b].ms - box.mods[a].ms;
			})
			.slice(0, 3)
			.map(function (k) {
				return { m: k, n: box.mods[k].n, s: Math.round(box.mods[k].ms / 1000) };
			});
		var acts = Object.keys(box.acts)
			.sort(function (a, b) {
				return box.acts[b] - box.acts[a];
			})
			.slice(0, 5)
			.map(function (k) {
				var cut = k.indexOf("|");
				return { m: k.slice(0, cut), b: k.slice(cut + 1), c: box.acts[k] };
			});
		var out = { v: 1, pages: box.pages, top: mods, acts: acts };
		if (box.gallery && box.gallery.length) out.gallery = box.gallery;
		if (buyEl) {
			var section = buyEl.closest ? buyEl.closest(".shopify-section") : null;
			out.buy = { p: PAGE, m: section ? moduleKey(section) : "", b: buyLabel || labelOf(buyEl) };
		}
		out.ts = new Date(Date.now()).toISOString();
		return JSON.stringify(out);
	}

	/*
	 * GA4 client id on the order (_soulog_ga_cid) - the same id GA4's BigQuery export calls
	 * user_pseudo_id, so an order can be joined to that buyer's full event history.
	 * Read from the _ga cookie ("GA1.1.<random>.<timestamp>" -> "<random>.<timestamp>").
	 * Same consent gate as the journey: "" without analytics consent, so it is never written then.
	 */
	var GA_ATTR = "_soulog_ga_cid";
	function gaClientId() {
		if (!journeyAllowed()) return "";
		try {
			var m = /(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/.exec(String(document.cookie || ""));
			return m ? m[1] : "";
		} catch (e) {
			return "";
		}
	}

	window.__soulogJourney = {
		attr: JOURNEY_ATTR,
		snapshot: snapshot,
		gaAttr: GA_ATTR,
		gaCid: gaClientId,
		noteGalleryImage: noteGalleryImage,
	};

	function setHidden(form, name, value) {
		var field = form.querySelector('input[name="' + name + '"]');
		if (!value) {
			if (field && field.parentNode) field.parentNode.removeChild(field);
			return;
		}
		if (!field) {
			field = document.createElement("input");
			field.type = "hidden";
			field.name = name;
			form.appendChild(field);
		}
		field.value = value;
	}

	var CART_ATTR_FIELD = "attributes[" + JOURNEY_ATTR + "]";
	var LINE_ATTR_FIELD = "properties[" + JOURNEY_ATTR + "]";
	var CART_GA_FIELD = "attributes[" + GA_ATTR + "]";
	var LINE_GA_FIELD = "properties[" + GA_ATTR + "]";

	document.addEventListener(
		"submit",
		function (event) {
			var form = event.target;
			if (!form || !form.getAttribute) return;
			var action = String(form.getAttribute("action") || "");
			/* Cart drawer / cart page "Check out": POST /cart with name=checkout updates the cart attributes, then redirects */
			if (/\/cart\/?$/.test(action) && event.submitter && event.submitter.name === "checkout") {
				setHidden(form, CART_ATTR_FIELD, snapshot(event.submitter));
				setHidden(form, CART_GA_FIELD, gaClientId());
				return;
			}
			/* Plain add-to-cart: drop the line fields, otherwise the same item would split into two cart lines */
			if (/\/cart\/add/.test(action)) {
				setHidden(form, LINE_ATTR_FIELD, "");
				setHidden(form, LINE_GA_FIELD, "");
			}
		},
		true,
	);

	/* Native dynamic checkout (Buy it now / Shop Pay) skips the cart and carries the product form's
	   line properties - fill the field the moment the button is pressed. PayPal sits inside an iframe
	   and never sends pointerdown here; the window-blur handler below fills the same fields for it. */
	document.addEventListener(
		"pointerdown",
		function (event) {
			var target = event.target;
			if (!target || !target.closest) return;
			var button = target.closest(".shopify-payment-button");
			var form = button && target.closest('form[action*="/cart/add"]');
			if (!form) return;
			setHidden(form, LINE_ATTR_FIELD, snapshot(button, "Express checkout"));
			setHidden(form, LINE_GA_FIELD, gaClientId());
		},
		true,
	);

	/* GA4 express_checkout_click (2026-10-20): native wallet buttons (Apple Pay / Shop Pay / Google Pay /
	   Buy it now) in the shop module and at the top of the cart page go straight to checkout without an
	   add_to_cart, so without this the click is invisible. payment_method comes from Shopify's
	   <shopify-xxx-button> host element (looked up through composedPath, the buttons may live in shadow DOM). */
	var lastExpress = { el: null, at: 0 };
	function walletMethod(nodes, wrap) {
		for (var i = 0; i < nodes.length; i++) {
			var el = nodes[i];
			var m = el && el.tagName && /^SHOPIFY-([A-Z-]+)-BUTTON$/.exec(el.tagName);
			if (m) return m[1].toLowerCase().replace(/-/g, "_");
			var cls = el && typeof el.className === "string" ? el.className : "";
			if (cls.indexOf("shopify-payment-button__more-options") > -1) return "more_options";
			if (cls.indexOf("shopify-payment-button__button--unbranded") > -1) return "buy_it_now";
			if (el === wrap) break;
		}
		return "unknown";
	}
	function reportWallet(wrap, method) {
		// One press can deliver several pointerdowns (pen / multi-touch); count it once
		var now = performance.now();
		if (lastExpress.el === wrap && now - lastExpress.at < 1000) return;
		lastExpress = { el: wrap, at: now };
		if (typeof window.__soulogGa4Event !== "function") return;
		window.__soulogGa4Event(
			"express_checkout_click",
			{
				checkout_location: wrap.closest(".shopify-payment-button") ? "product" : "cart",
				payment_method: method,
			},
			wrap,
		);
	}
	document.addEventListener(
		"pointerdown",
		function (event) {
			var target = event.target;
			if (!target || !target.closest) return;
			var wrap = target.closest(".shopify-payment-button, .additional-checkout-buttons");
			if (!wrap) return;
			var path = typeof event.composedPath === "function" ? event.composedPath() : [target];
			reportWallet(wrap, walletMethod(path, wrap));
		},
		true,
	);
	/* PayPal (2026-10-27) renders inside its own iframe, so its pointerdown never reaches this page.
	   Clicking into an iframe moves focus there and blurs the window: if the newly focused element is an
	   iframe inside a wallet area, that is the press. Walk up from the iframe to the <shopify-xxx-button>
	   host for the method. Focus stays in the iframe until the visitor clicks back on the page, so a second
	   PayPal click without leaving it is not counted again (one press per focus). */
	window.addEventListener("blur", function () {
		setTimeout(function () {
			var frame = document.activeElement;
			if (!frame || frame.tagName !== "IFRAME" || !frame.closest) return;
			var wrap = frame.closest(".shopify-payment-button, .additional-checkout-buttons");
			if (!wrap) return;
			var chain = [];
			for (var el = frame; el && el !== wrap; el = el.parentElement) chain.push(el);
			chain.push(wrap);
			// Product form: same journey fields the pointerdown path above fills for the other wallets
			var form = wrap.closest('form[action*="/cart/add"]');
			if (form && wrap.closest(".shopify-payment-button")) {
				setHidden(form, LINE_ATTR_FIELD, snapshot(wrap, "Express checkout"));
				setHidden(form, LINE_GA_FIELD, gaClientId());
			}
			reportWallet(wrap, walletMethod(chain, wrap));
		}, 0);
	});
	function send(name, p) {
		if (typeof window.__soulogGa4Event === "function") window.__soulogGa4Event(name, p);
	}

	var lastActivity = performance.now();
	function markActive() {
		lastActivity = performance.now();
	}
	["scroll", "wheel", "pointerdown", "pointermove", "keydown", "touchstart"].forEach(function (type) {
		window.addEventListener(type, markActive, { passive: true });
	});

	function videoPlaying(section) {
		var videos = section.querySelectorAll("video");
		for (var j = 0; j < videos.length; j++) {
			if (!videos[j].paused && !videos[j].ended) return true;
		}
		return false;
	}

	var lastTick = performance.now();
	function tick() {
		var now = performance.now();
		var delta = Math.min(now - lastTick, 1000);
		lastTick = now;
		if (document.visibilityState === "hidden") return;

		var vh = window.innerHeight;
		var top = null;
		var topVisible = 0;
		for (var j = 0; j < modules.length; j++) {
			var m = modules[j];
			var r = m.el.getBoundingClientRect();
			var visible = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
			if (visible <= 0) continue;
			if (!m.viewed && (visible >= vh / 2 || (r.height > 0 && visible >= r.height / 2))) {
				m.visibleMs += delta;
				if (m.visibleMs >= VIEW_MS) {
					m.viewed = true;
					send("module_view", params(m));
				}
			}
			if (m.dwell && visible > topVisible) {
				top = m;
				topVisible = visible;
			}
		}
		if (!top) return;
		if (now - lastActivity > IDLE_MS && !videoPlaying(top.el)) return;
		var room = CAP_MS - top.totalMs;
		if (room <= 0) return;
		var add = Math.min(delta, room);
		top.totalMs += add;
		top.pendingMs += add;
		trackDwell(top, add);
	}

	function flush() {
		for (var j = 0; j < modules.length; j++) {
			var m = modules[j];
			if (m.pendingMs < MIN_DWELL_MS) continue;
			var p = params(m);
			p.dwell_seconds = Math.round(m.pendingMs / 100) / 10;
			m.pendingMs = 0;
			send("module_dwell", p);
		}
		persistJourney();
	}

	document.addEventListener("visibilitychange", function () {
		if (document.visibilityState === "hidden") {
			flush();
		} else {
			lastTick = performance.now();
			markActive();
		}
	});
	/* Some browsers skip visibilitychange on unload; if both fire, pendingMs is already zero so nothing repeats */
	window.addEventListener("pagehide", flush);

	/*
	 * module_click - every clickable control inside a tracked module, collected automatically
	 * (choices, tabs, carousel dots / arrows, accordions, links...). Elements that already carry
	 * a dedicated data-ga4-click event are skipped so one click never counts twice. Only the
	 * control's own label is sent - never anything the visitor typed.
	 */
	var CLICKABLE =
		'a[href], button, summary, label, [role="button"], [role="tab"], [role="option"], [role="radio"], [role="switch"], [role="checkbox"], input[type="radio"], input[type="checkbox"]';
	var TYPE_BY_ROLE = { tab: "tab", option: "option", radio: "option", switch: "toggle", checkbox: "toggle" };

	function moduleOfControl(el) {
		var section = el.closest(".shopify-section");
		for (var j = 0; j < modules.length; j++) {
			if (modules[j].el === section) return modules[j];
		}
		return null;
	}

	/* innerText = what the visitor actually sees; textContent would also pull in hidden
	   desktop / mobile variants of the same label */
	function textOf(el) {
		if (!el) return "";
		var visible = typeof el.innerText === "string" ? el.innerText : el.textContent;
		return String(visible || "")
			.replace(/\s+/g, " ")
			.trim();
	}

	function nameOf(el) {
		var name = el.getAttribute("aria-label") || textOf(el) || el.getAttribute("title") || "";
		if (!name && el.querySelector) {
			var img = el.querySelector("img[alt]");
			if (img) name = img.getAttribute("alt") || "";
		}
		if (!name && el.tagName === "INPUT") {
			var label = el.closest("label");
			name = (label && textOf(label)) || el.getAttribute("value") || "";
		}
		return name ? name.slice(0, 100) : "(no label)";
	}

	function typeOf(el) {
		var tag = el.tagName;
		if (tag === "A") return "link";
		if (tag === "SUMMARY") return "summary";
		if (tag === "LABEL" || tag === "INPUT") return "option";
		if (tag === "SELECT") return "select";
		return TYPE_BY_ROLE[el.getAttribute("role")] || "button";
	}

	/* Shop module gallery (2026-10-26): clicks inside it also carry the image on screen (gallery_image) and the
	   last one the visitor picked by hand (gallery_picked), so a Buy Now can be tied to the photo that sold it.
	   The module element exposes ga4GalleryContext(); see assets/soulog-shop-module.js. */
	function galleryContext(el, p) {
		var host = el && el.closest ? el.closest("[data-ga4-gallery]") : null;
		if (!host || typeof host.ga4GalleryContext !== "function") return;
		try {
			var ctx = host.ga4GalleryContext();
			p.gallery_image = ctx.gallery_image || "";
			p.gallery_picked = ctx.gallery_picked || "";
		} catch (e) {}
	}

	function sendClick(m, el, name) {
		var p = params(m);
		p.button_name = name;
		p.element_type = typeOf(el);
		/* Stable id for controls whose label the merchant can edit (e.g. the buy button: "Buy Now" /
		   "Pre-order Now"). Reports key on it instead of the visible text (2026-10-21). */
		var key = el.getAttribute("data-ga4-button-key");
		if (key) p.button_key = key;
		if (p.element_type === "link") p.link_url = el.getAttribute("href") || "";
		galleryContext(el, p);
		send("module_click", p);
		trackAct(m, name);
	}

	/* Clicking a <label> makes the browser fire a second, synthetic click on its <input>. */
	var lastLabel = null;
	var lastLabelAt = 0;

	document.addEventListener(
		"click",
		function (event) {
			/* Only real visitor clicks: carousels / tabs sometimes drive themselves with el.click() or
			   dispatchEvent(new Event("click")), which would otherwise look like engagement. */
			if (event.isTrusted === false) return;
			var target = event.target;
			if (!target || !target.closest) return;
			var el = target.closest(CLICKABLE);
			if (!el || el.closest("[data-ga4-click]")) return;
			/* A label only counts as a choice when it wraps a radio / checkbox; a label for a text field is not */
			if (
				el.tagName === "LABEL" &&
				el.querySelector &&
				el.querySelector("input:not([type=radio]):not([type=checkbox]), textarea, select")
			)
				return;
			var m = moduleOfControl(el);
			if (!m) return;
			var now = performance.now();
			if (el.tagName === "INPUT") {
				var owner = el.closest("label");
				if (owner && owner === lastLabel && now - lastLabelAt < 500) return;
			}
			if (el.tagName === "LABEL") {
				lastLabel = el;
				lastLabelAt = now;
			}
			sendClick(m, el, nameOf(el));
		},
		true,
	);

	/* A <select> is reported on change with the option picked; opening it is not a choice yet. */
	document.addEventListener(
		"change",
		function (event) {
			if (event.isTrusted === false) return;
			var el = event.target;
			if (!el || el.tagName !== "SELECT" || !el.closest || el.closest("[data-ga4-click]")) return;
			var m = moduleOfControl(el);
			if (!m) return;
			var option = el.options && el.options[el.selectedIndex];
			var label = option
				? String(option.text || option.textContent || "")
						.replace(/\s+/g, " ")
						.trim()
				: "";
			sendClick(m, el, label || "(no label)");
		},
		true,
	);

	setInterval(tick, TICK_MS);
})();
